import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { DateField } from '@/components/ui/date-field';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const emptyVehicle = { unit: '', vin: '', make: '', model: '', year: '', mpg: '6.5', status: 'active' };

export default function FleetPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('vehicles');
  const [showAddV, setShowAddV] = useState(false);
  const [showAddM, setShowAddM] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [deleteId, setDeleteId] = useState('');

  const [vForm, setVForm] = useState(emptyVehicle);
  const [mForm, setMForm] = useState({ vehicle: '', description: '', cost: '', date: null });

  const fleetQ = useQuery({
    queryKey: ['fleet'],
    queryFn: async () => {
      const [v, m] = await Promise.all([
        api('/api/v1/fleet/vehicles'),
        api('/api/v1/fleet/maintenance'),
      ]);
      return {
        vehicles: v.status === 200 ? v.data.vehicles ?? [] : [],
        records: m.status === 200 ? m.data.records ?? [] : [],
      };
    },
  });

  const vehicles = fleetQ.data?.vehicles ?? [];
  const maintenance = fleetQ.data?.records ?? [];
  const deleteTarget = vehicles.find((v) => v.id === deleteId);

  const openCreateVehicle = () => {
    setEditingId('');
    setVForm(emptyVehicle);
    setShowAddV(true);
  };

  const openEditVehicle = (row) => {
    setEditingId(row.id);
    setVForm({
      unit: row.unitNumber ?? '',
      vin: row.vin ?? '',
      make: row.make ?? '',
      model: row.model ?? '',
      year: row.year != null ? String(row.year) : '',
      mpg: row.avgMpg != null ? String(row.avgMpg) : '6.5',
      status: row.status || 'active',
    });
    setShowAddV(true);
  };

  const handleSaveVehicle = async () => {
    if (!vForm.unit) {
      toast.error('Unit number is required');
      return;
    }
    const body = {
      unitNumber: vForm.unit.trim(),
      vin: vForm.vin || undefined,
      make: vForm.make || undefined,
      model: vForm.model || undefined,
      year: vForm.year ? Number(vForm.year) : undefined,
      avgMpg: vForm.mpg ? Number(vForm.mpg) : undefined,
      status: vForm.status || 'active',
    };
    const r = editingId
      ? await api(`/api/v1/fleet/vehicles/${editingId}`, { method: 'PATCH', body })
      : await api('/api/v1/fleet/vehicles', { method: 'POST', body });
    if (r.status === 200 || r.status === 201) {
      toast.success(editingId ? 'Vehicle updated' : `Vehicle ${vForm.unit} added`);
      setVForm(emptyVehicle);
      setEditingId('');
      setShowAddV(false);
      qc.invalidateQueries({ queryKey: ['fleet'] });
      qc.invalidateQueries({ queryKey: ['fleet-vehicles'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleDeactivateVehicle = async () => {
    if (!deleteId) return;
    const r = await api(`/api/v1/fleet/vehicles/${deleteId}`, { method: 'DELETE' });
    if (r.status === 200) {
      toast.success('Vehicle marked inactive');
      setDeleteId('');
      qc.invalidateQueries({ queryKey: ['fleet'] });
      qc.invalidateQueries({ queryKey: ['fleet-vehicles'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const handleAddMaint = async () => {
    if (!mForm.vehicle || !mForm.description) {
      toast.error('Vehicle and description are required');
      return;
    }
    const performedAt = mForm.date
      ? new Date(mForm.date).toISOString()
      : new Date().toISOString();
    const r = await api('/api/v1/fleet/maintenance', {
      method: 'POST',
      body: {
        vehicleId: mForm.vehicle,
        description: mForm.description.trim(),
        costCents: mForm.cost ? Math.round(parseFloat(mForm.cost) * 100) : undefined,
        performedAt,
      },
    });
    if (r.status === 201) {
      toast.success('Maintenance record logged');
      setMForm({ vehicle: '', description: '', cost: '', date: null });
      setShowAddM(false);
      qc.invalidateQueries({ queryKey: ['fleet'] });
      qc.invalidateQueries({ queryKey: ['fleet-vehicles'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed — safety role may be required'));
    }
  };

  const vehicleColumns = [
    { key: 'unitNumber', label: 'Unit #', sortable: true, className: 'font-mono' },
    {
      key: 'vin',
      label: 'VIN',
      className: 'font-mono text-xs',
      render: (v) => v || '—',
    },
    { key: 'make', label: 'Make', sortable: true, render: (v) => v || '—' },
    { key: 'model', label: 'Model', sortable: true, render: (v) => v || '—' },
    { key: 'year', label: 'Year', sortable: true, className: 'font-mono', render: (v) => v || '—' },
    {
      key: 'avgMpg',
      label: 'MPG',
      className: 'font-mono',
      render: (v) => (v != null ? v : '—'),
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-1 justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              openEditVehicle(row);
            }}
          >
            <Icons.Pencil className="h-3 w-3 mr-1" />
            Edit
          </Button>
          {row.status !== 'inactive' ? (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(row.id);
              }}
            >
              <Icons.Ban className="h-3 w-3 mr-1" />
              Deactivate
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const maintColumns = [
    {
      key: 'vehicleId',
      label: 'Vehicle',
      className: 'font-mono',
      render: (id) => vehicles.find((v) => v.id === id)?.unitNumber ?? id?.slice?.(0, 8) ?? '—',
    },
    { key: 'description', label: 'Description' },
    {
      key: 'costCents',
      label: 'Cost',
      render: (v) =>
        v != null ? <span className="font-mono">{formatCurrency(v / 100)}</span> : '—',
    },
    {
      key: 'performedAt',
      label: 'Date',
      render: (v) => (v ? formatDate(v) : '—'),
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Fleet"
          description="Units and maintenance — keep VIN/MPG current and deactivate retired equipment."
          icon="Truck"
          metrics={[
            { label: 'Vehicles', value: vehicles.length },
            { label: 'Active', value: vehicles.filter((v) => v.status === 'active').length },
            { label: 'Maintenance', value: maintenance.length },
          ]}
          actions={
            <Button
              onClick={() =>
                tab === 'vehicles' ? openCreateVehicle() : setShowAddM(!showAddM)
              }
            >
              <Icons.Plus className="h-4 w-4 mr-2" />
              {tab === 'vehicles' ? 'Add Vehicle' : 'Log Maintenance'}
            </Button>
          }
        />

        <div className="inline-flex items-center gap-1 p-1 bg-muted rounded-lg">
          {['vehicles', 'maintenance'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-all',
                tab === t
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'vehicles' ? (
          <>
            {showAddV && (
              <Card>
                <CardHeader>
                  <CardTitle>{editingId ? 'Edit Vehicle' : 'New Vehicle'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FloatingField
                      label="Unit Number"
                      value={vForm.unit}
                      onChange={(e) => setVForm({ ...vForm, unit: e.target.value })}
                      required
                      data-testid="vehicle-unit-input"
                    />
                    <FloatingField
                      label="VIN (17 chars)"
                      value={vForm.vin}
                      onChange={(e) => setVForm({ ...vForm, vin: e.target.value })}
                      maxLength={17}
                    />
                    <FloatingField
                      label="Make"
                      value={vForm.make}
                      onChange={(e) => setVForm({ ...vForm, make: e.target.value })}
                    />
                    <FloatingField
                      label="Model"
                      value={vForm.model}
                      onChange={(e) => setVForm({ ...vForm, model: e.target.value })}
                    />
                    <FloatingField
                      label="Year"
                      type="number"
                      value={vForm.year}
                      onChange={(e) => setVForm({ ...vForm, year: e.target.value })}
                    />
                    <FloatingField
                      label="Average MPG"
                      type="number"
                      step="0.1"
                      value={vForm.mpg}
                      onChange={(e) => setVForm({ ...vForm, mpg: e.target.value })}
                    />
                    {editingId ? (
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={vForm.status}
                          onChange={(e) => setVForm({ ...vForm, status: e.target.value })}
                        >
                          <option value="active">Active</option>
                          <option value="maintenance">Maintenance</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    ) : null}
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddV(false);
                        setEditingId('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleSaveVehicle} data-testid="save-vehicle-button">
                      {editingId ? 'Save changes' : 'Add Vehicle'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <DataTable
              columns={vehicleColumns}
              data={vehicles}
              emptyMessage={
                fleetQ.isLoading ? 'Loading…' : 'No vehicles yet — add a unit to use Dispatch.'
              }
            />
            <ConfirmDialog
              open={Boolean(deleteId)}
              onOpenChange={(open) => !open && setDeleteId('')}
              title="Deactivate vehicle?"
              description={`Mark unit ${deleteTarget?.unitNumber || ''} inactive? It stays on past trips but leaves active dispatch pickers.`}
              confirmLabel="Deactivate"
              variant="destructive"
              onConfirm={handleDeactivateVehicle}
            />
          </>
        ) : (
          <>
            {showAddM && (
              <Card>
                <CardHeader>
                  <CardTitle>Log Maintenance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Vehicle *
                      </label>
                      <Combobox
                        options={vehicles.map((v) => ({
                          value: v.id,
                          label: `${v.unitNumber}${v.make ? ` - ${v.make} ${v.model || ''}` : ''}`,
                        }))}
                        value={mForm.vehicle}
                        onChange={(v) => setMForm({ ...mForm, vehicle: v })}
                        placeholder="Select vehicle"
                      />
                    </div>
                    <FloatingField
                      label="Cost ($)"
                      type="number"
                      step="0.01"
                      value={mForm.cost}
                      onChange={(e) => setMForm({ ...mForm, cost: e.target.value })}
                    />
                    <div className="md:col-span-2">
                      <FloatingField
                        label="Description"
                        value={mForm.description}
                        onChange={(e) => setMForm({ ...mForm, description: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Service Date
                      </label>
                      <DateField value={mForm.date} onChange={(d) => setMForm({ ...mForm, date: d })} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowAddM(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddMaint}>Log Maintenance</Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <DataTable
              columns={maintColumns}
              data={maintenance}
              emptyMessage={fleetQ.isLoading ? 'Loading…' : 'No maintenance records'}
            />
          </>
        )}
      </div>
);
}
